import type { JSX } from 'react';
import React from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useChatListStore } from '../../stores/chatListStore';
import { ToastType } from '../../types/ui';

/**
 * Toast UI component used by a dual-path routing model.
 *
 * Why: Avoids clipping under modals and keeps contextual toasts anchored near chat.
 * How: Placement drives routing — global toasts render via a portal (bottom-center,
 * above modals); chat toasts render inline within the chat container. Certain types
 * expose actions (upload cancel, export open/show) by delegating to context handlers.3
 *
 * @example Basic usage (inline in chat area)
 * ```tsx
 * const { showToast } = useToastStore();
 * showToast('Saved', 'success', { timeout: 2000, placement: 'chat' });
 * ```
 *
 * @example Global usage above modals
 * ```tsx
 * showToast('Export complete', 'export_success', {
 *   timeout: 5000,
 *   placement: 'bottom-center'
 * });
 * ```
 */

/**
 * Props are intentionally minimal; types communicate the contract.
 */
interface ToastProps {
	isVisible: boolean;
	message: string;
	type: string;
	placement?: 'bottom-center' | 'chat';
	inPortal?: boolean;
	progress?: number;
}

/**
 * Renders a toast with style and actions derived from `type`, and positions it
 * based on `inPortal`/`placement`. Pointer events are disabled on wrappers to
 * prevent overlay blocking; buttons opt back into interaction.
 */
export function Toast({
	isVisible,
	message,
	type,
	placement = 'bottom-center',
	inPortal,
	progress,
}: ToastProps): JSX.Element | null {
	const { handleFileUploadAbort } = useDocumentStore();
	const handleExportOpenFile = useChatListStore((s) => s.handleExportOpenFile);
	const handleExportShowInFolder = useChatListStore((s) => s.handleExportShowInFolder);

	if (!isVisible) return null;

	const getToastStyles = () => {
		const baseClasses = 'rounded-large px-md py-xs shadow-large border';
		const styleMap: Record<ToastType, string> = {
			success:
				'bg-status-success text-neutral-white border-status-success',
			error: 'bg-status-error text-neutral-white border-status-error',
			warning: 'bg-status-warning text-neutral-700 border-status-warning',
			loading: 'bg-status-info text-neutral-200 border-status-info',
			upload: 'bg-status-info text-neutral-200 border-status-info',
			info: 'bg-status-info text-neutral-200 border-status-info',
			export_success:
				'bg-status-success text-neutral-white border-status-success',
			export_bulk_success:
				'bg-status-success text-neutral-white border-status-success',
            export_warning:
                'bg-status-warning text-neutral-white border-status-warning',
		};
		const specificClasses = styleMap[type as ToastType] || styleMap.info;
		return `${baseClasses} ${specificClasses}`;
	};

	const getToastIcon = () => {
		switch (type) {
			case 'success':
			case 'export_success':
			case 'export_bulk_success':
				return <i className="fas fa-check-circle" />;
			case 'error':
				return <i className="fas fa-exclamation-circle" />;
			case 'warning':
			case 'export_warning':
				return <i className="fas fa-exclamation-triangle" />;
			case 'loading':
				return <i className="fas fa-spinner fa-spin" />;
			case 'upload':
				return <i className="fas fa-spinner fa-spin" />;
			case 'info':
			default:
				return <i className="fas fa-info-circle" />;
		}
	};

	// Positioning: portal centers within its fixed container; inline renders at bottom center of its own layout context
	const getPositionClass = () => {

		if (inPortal) {
			// Portal sets fixed container; we only center the toast content
			return 'flex justify-center pointer-events-none';
		}

		// Inline positioning relative to chat or app container
		switch (placement) {
			case 'chat':
				return `absolute bottom-24 left-1/2 transform -translate-x-1/2 pointer-events-none transition-all duration-300`;
			case 'bottom-center':
			default:
				return `absolute bottom-24 left-1/2 transform -translate-x-1/2 pointer-events-none transition-all duration-300`;
		}
	};

	const wrapperPositionClass = getPositionClass();

	const buttonStyles =
		'flex justify-center items-center bg-neutral-600 border border-neutral-600 rounded-[15px] px-3 py-1 min-w-[70px] h-7 cursor-pointer transition-colors duration-200 ml-2 text-[var(--basic-200)] text-sm hover:bg-red-500 hover:border-red-500 hover:text-neutral-100';

	// Split bulk export messages into summary and path for clearer layout
	const formatMessage = () => {
		if ((type === 'export_bulk_success' || type === 'export_warning') && message.includes(' to ')) {
			const parts = message.split(' to ');
			if (parts.length === 2) {
				return {
					summary: parts[0],
					path: parts[1]
				};
			}
		}
		return { summary: message, path: null };
	};

	const { summary, path } = formatMessage();
	const isElectron = typeof window !== 'undefined' && 'electron' in window;

	return (
		<div className={`${wrapperPositionClass} z-[10001]`}>
			<div className="pointer-events-auto transform transition-all duration-300 ease-out">
				<div className={getToastStyles()}>
					{/* Multi-line layout for bulk exports */}
					{(type === 'export_bulk_success' || type === 'export_warning') && path ? (
						<div className="space-y-xs">
							<div className="flex justify-between items-center">
								<p className="text-sm font-medium">{summary}</p>
								{getToastIcon()}
							</div>
							<div className="flex justify-between items-center">
								<p className="text-xs text-neutral-100 break-all mr-xs">
									{path}
								</p>
								{isElectron && (
									<button
										className={buttonStyles}
										onClick={handleExportShowInFolder}
										type="button"
									>
										Show in folder
									</button>
								)}
							</div>
						</div>
					) : (
						/* Single-line layout for other toast types */
						<div>
							<div className="flex justify-center items-center">
								{type === 'upload' && (
									<i className="fas fa-cloud-upload-alt text-neutral-100 mr-xs" />
								)}
								<p className="mr-xs text-neutral-100">{message}</p>
								{getToastIcon()}
								{type === 'upload' && (
									<button
										className={buttonStyles}
										onClick={handleFileUploadAbort}
										type="button"
									>
										Cancel
									</button>
								)}
								{type === 'export_success' && isElectron && (
									<div className="flex items-center">
										<button
											className={buttonStyles}
											onClick={handleExportOpenFile}
											type="button"
										>
											Open file
										</button>
										<button
											className={buttonStyles}
											onClick={handleExportShowInFolder}
											type="button"
										>
											Show in folder
										</button>
									</div>
								)}
							</div>
							{/* Progress bar in separate row */}
							{type === 'upload' && progress !== undefined && (
								<div className="mt-2 w-full">
									<div className="bg-neutral-600 rounded-full h-2 overflow-hidden">
										<div
											className="h-full bg-red-600 transition-all duration-300 ease-out"
											style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
										/>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}